import React, { useEffect, useState } from "react";
import { Navbar, Footer } from "@/components/yc/Nav";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, Sparkles, ChevronDown } from "lucide-react";

const EnquiryModal = ({ open, onClose, plan }) => {
    const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
    const [sending, setSending] = useState(false);
    if (!open) return null;
    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await api.post("/public/enquiry", {
                kind: "corporate",
                ...form,
                message: `[Apply: ${plan || "plan"}] ${form.message}`,
            });
            toast.success("Thanks. We will be in touch to book your onboarding.");
            onClose();
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : detail?.[0]?.msg || "Could not send. Check your email address");
        } finally { setSending(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-yc-ink/60 flex items-center justify-center p-4" data-testid="apply-modal">
            <form onSubmit={submit} className="yc-card p-8 bg-yc-cream w-full max-w-md relative">
                <button type="button" onClick={onClose} data-testid="apply-close" className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
                <div className="yc-overline">{plan ? `Apply for ${plan}` : "Apply"}</div>
                <h3 className="font-heading text-2xl font-black tracking-tighter mt-1">Book onboarding</h3>
                <p className="text-sm text-yc-ink/70 mt-2">Tell us a little about your team and we will set up your free onboarding.</p>
                <div className="space-y-3 mt-5">
                    <input data-testid="apply-name" required className="yc-input" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    <input data-testid="apply-email" required type="email" className="yc-input" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                    <input data-testid="apply-company" className="yc-input" placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                    <textarea data-testid="apply-message" rows={3} className="yc-input" placeholder="Website, your role, team size" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                </div>
                <button data-testid="apply-submit" disabled={sending} className="yc-btn-primary w-full mt-5">{sending ? "Sending…" : "Send"}</button>
            </form>
        </div>
    );
};

const Cell = ({ value }) => {
    if (value === true) return <Check className="w-5 h-5 text-yc-blue mx-auto" />;
    if (value === false) return <X className="w-5 h-5 text-yc-ink/30 mx-auto" />;
    return <span className="text-sm">{value}</span>;
};

const Pricing = () => {
    const [data, setData] = useState(null);
    const [compare, setCompare] = useState(false);
    const [applyFor, setApplyFor] = useState(null); // plan name or null

    useEffect(() => { api.get("/public/plans").then((r) => setData(r.data)).catch(() => {}); }, []);

    const plans = data?.plans || [];
    const founding = data?.founding;
    const foundingLive = founding && founding.left > 0;

    const priceBlock = (p) => {
        if (p.poa) return (
            <div>
                <div className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">£POA</div>
                <div className="text-sm text-yc-ink/60 mt-1">Billed annually</div>
            </div>
        );
        return (
            <div>
                <div className="font-heading font-black text-4xl sm:text-5xl tracking-tighter">£{p.price_monthly.toLocaleString()}<span className="text-lg font-bold">/mo</span></div>
                <div className="text-sm text-yc-ink/60 mt-1">Billed annually</div>
                {foundingLive && p.founding_price_monthly && (
                    <div className="mt-2 text-sm font-semibold text-yc-pink" data-testid={`founding-price-${p.id}`}>
                        Founding Member: £{p.founding_price_monthly.toLocaleString()}/mo for year one
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-yc-cream flex flex-col">
            <Navbar />
            <EnquiryModal open={!!applyFor} onClose={() => setApplyFor(null)} plan={applyFor} />

            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-10 w-full">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="yc-overline">Membership</div>
                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-3">Join the 1000s of other businesses and build your marketing team today.</h1>
                    <p className="text-yc-ink/70 mt-4 text-base">
                        Annual access to our vetted UK network of graphic designers, illustrators, social media managers,
                        content creators, motion designers, animators, photographers and videographers. Use credits to unlock contact details and creator data.
                    </p>
                    <p className="text-xs text-yc-ink/50 mt-3">{data?.vat_note || "Prices exclude VAT. 20% is added at checkout."}</p>
                </div>

                {foundingLive && (
                    <div className="mt-8 yc-card p-5 bg-yc-pink text-white flex flex-col sm:flex-row items-center justify-center gap-3 text-center" data-testid="founding-banner">
                        <Sparkles className="w-5 h-5" />
                        <span className="font-heading font-bold">Founding offer: the first 20 businesses get 25% off year one, locked for 2 years.</span>
                        <span className="yc-badge bg-white text-yc-ink" data-testid="founding-left">{founding.left} of {founding.total} founding places left</span>
                    </div>
                )}

                {/* Choose a plan heading */}
                <div className="mt-12">
                    <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter" data-testid="choose-plan-heading">Choose a plan</h2>
                    <p className="text-yc-ink/60 mt-2 text-base">Not sure? Our friendly team will help you pick the perfect plan.</p>
                </div>

                {/* Plan cards */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                    {plans.map((p) => (
                        <div key={p.id} data-testid={`plan-card-${p.id}`}
                            className={`yc-card p-8 flex flex-col relative ${p.popular ? "border-yc-blue border-4" : ""}`}>
                            {p.popular && <span className="yc-badge bg-yc-blue text-white absolute -top-3 left-8">Most popular</span>}
                            <h2 className="font-heading text-2xl font-black tracking-tighter">{p.name}</h2>
                            <p className="text-sm text-yc-ink/60 mt-1 min-h-[40px]">{p.tagline}</p>
                            <div className="mt-5">{priceBlock(p)}</div>
                            <div className="mt-5 space-y-1.5 text-sm">
                                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-yc-blue" /> {p.users} users</div>
                                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-yc-blue" /> {p.credits} credits a month</div>
                                <div className="flex items-center gap-2"><Check className="w-4 h-4 text-yc-blue" /> Full vetted network access</div>
                            </div>
                            <button data-testid={`apply-${p.id}`} onClick={() => setApplyFor(p.name)}
                                className={`w-full mt-6 ${p.popular ? "yc-btn-primary" : "yc-btn-secondary"}`}>Apply now</button>
                            <button data-testid={`compare-${p.id}`} onClick={() => setCompare((v) => !v)}
                                className="text-sm text-yc-blue font-semibold mt-3 inline-flex items-center justify-center gap-1">
                                Compare plans <ChevronDown className={`w-4 h-4 transition-transform ${compare ? "rotate-180" : ""}`} />
                            </button>
                        </div>
                    ))}
                </div>

                {/* Comparison tables */}
                {compare && data && (
                    <div className="mt-12 space-y-10" data-testid="compare-tables">
                        <div>
                            <h3 className="font-heading text-2xl font-black tracking-tighter mb-4">Access</h3>
                            <div className="yc-card overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm min-w-[640px]">
                                        <thead className="bg-yc-cream border-b-2 border-yc-ink">
                                            <tr>
                                                <th className="text-left p-4">&nbsp;</th>
                                                <th className="p-4">Starter</th><th className="p-4">Growth</th><th className="p-4">Enterprise</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.access_rows.map((r) => (
                                                <tr key={r.label} className="border-b border-yc-ink/10">
                                                    <td className="text-left p-4 font-semibold">{r.label}</td>
                                                    <td className="p-4 text-center"><Cell value={r.starter} /></td>
                                                    <td className="p-4 text-center"><Cell value={r.growth} /></td>
                                                    <td className="p-4 text-center"><Cell value={r.enterprise} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div>
                            <h3 className="font-heading text-2xl font-black tracking-tighter mb-4">Features</h3>
                            <div className="yc-card overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm min-w-[640px]">
                                        <thead className="bg-yc-cream border-b-2 border-yc-ink">
                                            <tr>
                                                <th className="text-left p-4">&nbsp;</th>
                                                <th className="p-4">Starter</th><th className="p-4">Growth</th><th className="p-4">Enterprise</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.feature_rows.map((r) => (
                                                <tr key={r.label} className="border-b border-yc-ink/10">
                                                    <td className="text-left p-4 font-semibold">{r.label}</td>
                                                    <td className="p-4 text-center"><Cell value={r.starter} /></td>
                                                    <td className="p-4 text-center"><Cell value={r.growth} /></td>
                                                    <td className="p-4 text-center"><Cell value={r.enterprise} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Credits explainer */}
                <div className="mt-14 yc-card p-8" data-testid="credits-explainer">
                    <h3 className="font-heading text-2xl font-black tracking-tighter">How credits work</h3>
                    <p className="text-sm text-yc-ink/70 mt-2">Each unlock costs 2 credits per creator. Credits are shared across your team, reset on the 1st of each month and do not roll over. Once you unlock something for a creator it stays unlocked for your account.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-5 text-sm">
                        {["Telephone number", "Email address", "Hiring insights (rate history, turnaround, availability)", "Job turnover (jobs completed, repeat bookings)", "Reliability (on time delivery, brand ratings, Punctuality score)"].map((x) => (
                            <div key={x} className="flex items-start gap-2"><Check className="w-4 h-4 text-yc-blue mt-0.5 shrink-0" /> {x}</div>
                        ))}
                    </div>
                    <p className="text-xs text-yc-ink/50 mt-4">Top ups: 50 credits from £75, bought instantly by card.</p>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Pricing;
